import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type {
  TripConfig,
  NotificationPriority,
  NotificationCategory,
  NotificationDeepLink,
} from '../types';
import { numberWord } from '../utils/numberWords';

/**
 * Low-level client → callable bridge. Notification documents trigger OS push,
 * so clients can no longer write them directly (Firestore create rule is now
 * `if false`). The createTripNotifications Cloud Function authorizes the caller
 * against the trip + recipients server-side, stamps trusted fields (senderUid,
 * createdAt, expiry, tripId), and writes via the Admin SDK.
 *
 * Errors are swallowed-and-logged: a notification failing must never break the
 * underlying action (posting a memory, declining an invite, etc.).
 */
export interface CreateNotifInput {
  tripId: string;
  recipientIds: string[];
  title: string;
  message: string;
  type?: string;
  category: NotificationCategory;
  priority: NotificationPriority;
  actionRequired?: boolean;
  /** Attribution + deep-link payload (tripTitle, creatorId, senderName, emoji, deepLink, …). */
  data?: Record<string, any>;
  /** Deterministic dedupe key: retries and repeat detections notify once. */
  key?: string;
}

export async function createTripNotifications(input: CreateNotifInput): Promise<void> {
  const recipientIds = [...new Set(input.recipientIds)].filter(Boolean);
  if (recipientIds.length === 0) return;
  // A private branch scope (trips/{root}/branches/{id}) goes to the server as
  // root + branchId, which limits sender and recipients to that branch's members.
  const [tripId, nested, branchId] = input.tripId.split('/');
  if (nested !== undefined && (nested !== 'branches' || !branchId)) return;
  try {
    const fn = httpsCallable(functions, 'createTripNotifications');
    await fn({ ...input, tripId, ...(branchId ? { branchId } : {}), recipientIds });
  } catch (err) {
    console.error('createTripNotifications failed', err);
  }
}

/** Returns the first word of a name — ensures notification copy uses first names. */
function fn(name: string) {
  return (name || 'Someone').split(' ')[0];
}

export interface NotifyOptions {
  tripId: string;
  tripTitle?: string;
  /** User IDs to receive the notification — sender is automatically excluded. */
  recipientIds: string[];
  /** User ID of whoever triggered the action — excluded from recipients. */
  senderId: string;
  /** Display name of the sender, stored for the attribution tag in the notification centre. */
  senderName?: string;
  title: string;
  message: string;
  priority: NotificationPriority;
  category: NotificationCategory;
  /** Custom emoji to display instead of the category icon. */
  emoji?: string;
  type?: 'invite' | 'info' | 'import' | 'expense' | 'itinerary' | 'social' | 'reminder';
  /** If true, notification stays prominent in the centre until actioned. */
  actionRequired?: boolean;
  deepLink?: NotificationDeepLink;
  /** Any extra fields to store in data. */
  extra?: Record<string, any>;
}

/**
 * Core notification sender. Filters out the sender, writes one Firestore
 * document per recipient under users/{uid}/notifications.
 */
export async function notifyUsers(opts: NotifyOptions): Promise<void> {
  const recipients = [...new Set(opts.recipientIds)].filter(id => id !== opts.senderId);
  if (recipients.length === 0) return;

  await createTripNotifications({
    tripId: opts.tripId,
    recipientIds: recipients,
    title: opts.title,
    message: opts.message,
    type: opts.type ?? 'info',
    category: opts.category,
    priority: opts.priority,
    actionRequired: opts.actionRequired ?? false,
    data: {
      tripTitle: opts.tripTitle ?? '',
      creatorId: opts.senderId,
      senderName: opts.senderName ?? '',
      emoji: opts.emoji ?? null,
      deepLink: opts.deepLink ?? null,
      ...(opts.extra ?? {}),
    },
  });
}

// ─── Convenience wrappers ────────────────────────────────────────────────────

/** Full booking editors retain admin rights. Notify only the admin's own
 * deliberate participation delta, not another traveller's concurrent change.
 * Delivery follows the existing best-effort booking-edit notification path. */
export async function notifyOwnBookingParticipation(
  trip: Pick<TripConfig, 'id' | 'title' | 'ownerId' | 'memberIds'>,
  actor: { id: string; name: string },
  previous: { participantIds?: string[]; contributedBy?: string },
  edited: { participantIds?: string[] },
  saved: { participantIds?: string[]; id?: string; name?: string; carrier?: string; mode?: string },
  dayDate: string,
  section: 'transport' | 'accommodation',
) {
  const included = (item: { participantIds?: string[] }) => !item.participantIds || item.participantIds.includes(actor.id);
  const creator = previous.contributedBy || trip.ownerId;
  if (creator === actor.id || !trip.memberIds.includes(creator) || included(previous) === included(edited)
    || included(edited) !== included(saved)) return;
  await notifyUsers({
    tripId: trip.id, tripTitle: trip.title, recipientIds: [creator], senderId: actor.id, senderName: fn(actor.name),
    title: 'Participation updated',
    message: `${fn(actor.name)} ${included(saved) ? 'is joining' : 'is no longer joining'} ${saved.name || saved.carrier || saved.mode || 'a booking'}.`,
    priority: 'P2', category: 'itinerary', type: 'itinerary',
    deepLink: { tab: 'logistics', dayDate, section, itemId: saved.id },
  });
}

/** Someone accepted a trip invite — notify all members. */
export async function notifyInviteAccepted(
  tripId: string,
  tripTitle: string,
  joineeId: string,
  joineeName: string,
  allMemberIds: string[]
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: allMemberIds,
    senderId: joineeId,
    senderName: fn(joineeName),
    title: 'New trip member',
    message: `${fn(joineeName)} joined the **${tripTitle}** trip`,
    priority: 'P1',
    category: 'trip-admin',
    type: 'info',
    deepLink: { tab: 'bucketlist' },
  });
}

/** Trip dates changed. */
export async function notifyTripDatesChanged(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  allMemberIds: string[],
  newDates: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: allMemberIds,
    senderId,
    senderName: fn(senderName),
    title: 'Trip dates updated',
    message: `${fn(senderName)} updated the _${tripTitle}_ trip dates to **${newDates}**`,
    priority: 'P1',
    category: 'trip-admin',
    type: 'itinerary',
    deepLink: { tab: 'bucketlist' },
  });
}

/** Trip renamed. */
export async function notifyTripRenamed(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  allMemberIds: string[],
  newTitle: string
) {
  await notifyUsers({
    tripId, tripTitle: newTitle,
    recipientIds: allMemberIds,
    senderId,
    senderName: fn(senderName),
    title: 'Trip renamed',
    message: `${fn(senderName)} renamed the trip to **${newTitle}**`,
    priority: 'P1',
    category: 'trip-admin',
    type: 'info',
    deepLink: { tab: 'bucketlist' },
  });
}

/** Destination added. */
export async function notifyDestinationAdded(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  allMemberIds: string[],
  destName: string,
  emoji: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: allMemberIds,
    senderId,
    senderName: fn(senderName),
    title: 'Destination added',
    message: `${fn(senderName)} added **${destName}** to the _${tripTitle}_ trip`,
    priority: 'P1',
    category: 'trip-admin',
    emoji,
    type: 'itinerary',
    deepLink: { tab: 'logistics' },
    extra: { destName }, // stored so UI can do live emoji lookup from trip config
  });
}

/** Destination removed. */
export async function notifyDestinationRemoved(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  allMemberIds: string[],
  destName: string,
  emoji: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: allMemberIds,
    senderId,
    senderName: fn(senderName),
    title: 'Destination removed',
    message: `${fn(senderName)} removed **${destName}** from the _${tripTitle}_ trip`,
    priority: 'P1',
    category: 'trip-admin',
    emoji, // stored as fallback — destination is gone so live lookup will miss
    type: 'itinerary',
    deepLink: { tab: 'logistics' },
    extra: { destName },
  });
}

/**
 * Format a transport endpoint for notification copy.
 * Priority: fromCity → full `from` text → code → fallback label.
 * Ensures we never produce a blank or code-only string.
 */
function transportPoint(city: string | undefined, full: string | undefined, code: string | undefined, fallback: string): string {
  if (city && city.trim()) return city.trim();
  if (full && full.trim()) return full.trim();
  if (code && code.trim()) return code.trim();
  return fallback;
}

/** Transport added to a day. */
export async function notifyTransportAdded(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  dayTravellerIds: string[],
  label: string,
  dayDate: string,
  emoji: string = '✈️',
  itemId?: string,
  fromCity?: string,
  fromFull?: string,
  fromCode?: string,
  toCity?: string,
  toFull?: string,
  toCode?: string,
) {
  const from = transportPoint(fromCity, fromFull, fromCode, 'Departure');
  const to   = transportPoint(toCity,   toFull,   toCode,   'Arrival');
  // Use route copy when we have meaningful endpoints; fall back to the raw label
  const routeText = (from !== 'Departure' || to !== 'Arrival')
    ? `${from} → ${to}`
    : label;

  await notifyUsers({
    tripId, tripTitle,
    recipientIds: dayTravellerIds,
    senderId,
    senderName: fn(senderName),
    title: 'Transport added',
    message: `${fn(senderName)} added **${routeText}** to your _${tripTitle}_ itinerary`,
    priority: 'P2',
    category: 'itinerary',
    emoji,
    type: 'itinerary',
    deepLink: { tab: 'logistics', dayDate, section: 'transport', itemId },
  });
}

/** Any itinerary card's date was changed. Itinerary update — daily digest push (P2). */
export async function notifyItemDateChanged(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  recipientIds: string[],
  itemLabel: string,
  newDate: string,
  dayDate: string,
  section: 'transport' | 'accommodation' | 'attractions' | 'eats',
  emoji?: string,
  itemId?: string
) {
  // Same split as the deep-link below: transport/accommodation live on the
  // Itinerary tab, attractions/eats live on the Shortlist tab.
  const onLogistics = section === 'transport' || section === 'accommodation';
  await notifyUsers({
    tripId, tripTitle,
    recipientIds,
    senderId,
    senderName: fn(senderName),
    title: 'Date changed',
    message: `${fn(senderName)} rescheduled **${itemLabel}** to ${newDate}`,
    priority: 'P2',
    category: onLogistics ? 'itinerary' : 'shortlist',
    emoji,
    type: 'itinerary',
    deepLink: { tab: onLogistics ? 'logistics' : 'bucketlist', dayDate, section, itemId },
  });
}

/** Accommodation added to a destination. */
export async function notifyAccommodationAdded(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  dayTravellerIds: string[],
  accomName: string,
  location: string,
  dayDate: string,
  emoji: string = '🏨',
  itemId?: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: dayTravellerIds,
    senderId,
    senderName: fn(senderName),
    title: 'Accommodation added',
    message: `${fn(senderName)} added **${accomName}** to your ${location} itinerary`,
    priority: 'P2',
    category: 'itinerary',
    emoji,
    type: 'itinerary',
    deepLink: { tab: 'logistics', dayDate, section: 'accommodation', itemId },
  });
}

/** Attraction or eat shortlisted (not yet committed to plan). */
export async function notifyShortlisted(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  allMemberIds: string[],
  itemName: string,
  location: string,
  dayDate: string,
  section: 'attractions' | 'eats',
  emoji: string,
  itemId?: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: allMemberIds,
    senderId,
    senderName: fn(senderName),
    title: 'Shortlisted',
    message: `${fn(senderName)} shortlisted **${itemName}** for your ${location} itinerary`,
    priority: 'P3',
    category: 'shortlist',
    emoji,
    type: 'itinerary',
    deepLink: { tab: 'bucketlist', dayDate, section, itemId },
  });
}

/** Attraction or eat committed to the Schedule (moved from shortlist). */
export async function notifyAddedToSchedule(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  allMemberIds: string[],
  itemName: string,
  location: string,
  dayDate: string,
  section: 'attractions' | 'eats',
  emoji: string,
  itemId?: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: allMemberIds,
    senderId,
    senderName: fn(senderName),
    title: 'Added to Schedule',
    message: `${fn(senderName)} added **${itemName}** to your ${location} itinerary`,
    priority: 'P2',
    category: 'shortlist',
    emoji,
    type: 'itinerary',
    deepLink: { tab: 'bucketlist', dayDate, section, itemId },
  });
}

/** New expense logged — notifies everyone in the split. */
export async function notifyExpenseLogged(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  splitIds: string[],
  desc: string,
  amount: string,
  currency: string,
  emoji: string,
  itemId?: string
) {
  const othersCount = splitIds.length - 2;
  const othersText = othersCount > 0 ? (othersCount === 1 ? ' and one other' : ` and ${numberWord(othersCount)} others`) : '';
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: splitIds,
    senderId,
    senderName: fn(senderName),
    title: 'New expense split',
    message: `${fn(senderName)} split **${amount} ${currency}** with you${othersText} for ${desc}`,
    priority: 'P1',
    category: 'expenses',
    emoji,
    type: 'expense',
    deepLink: { tab: 'expenses', itemId },
  });
}

/** Expense edited (any field). Personal impact — push (P1). */
export async function notifyExpenseEdited(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  splitIds: string[],
  desc: string,
  emoji: string,
  itemId?: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: splitIds,
    senderId,
    senderName: fn(senderName),
    title: 'Expense updated',
    message: `${fn(senderName)} updated the **${desc}** expense`,
    priority: 'P1',
    category: 'expenses',
    emoji,
    type: 'expense',
    deepLink: { tab: 'expenses', itemId },
  });
}

/** Expense deleted — notifies former split members. */
export async function notifyExpenseDeleted(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  formerSplitIds: string[],
  desc: string,
  emoji: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: formerSplitIds,
    senderId,
    senderName: fn(senderName),
    title: 'Expense removed',
    message: `${fn(senderName)} removed the **${desc}** expense`,
    priority: 'P1',
    category: 'expenses',
    emoji,
    type: 'expense',
    deepLink: { tab: 'expenses' },
  });
}


/**
 * Per-share settlement notification (mark / unmark). Personal impact — push (P1).
 * The confirm/pending model has been removed; settlements take effect immediately.
 */
export async function notifySettlement(
  tripId: string,
  tripTitle: string,
  actorId: string,
  actorName: string,
  recipientId: string,
  title: string,
  message: string,
  /** Reuses the generic deep-link `section` field (no itinerary meaning
   *  here) so tapping a "Settle all" notification lands the recipient on
   *  the Settle sub-tab, scrolled to where their settlement now sits. */
  settleSection?: 'settled-received' | 'settled-paid',
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: [recipientId],
    senderId: actorId,
    senderName: fn(actorName),
    title,
    message,
    priority: 'P1',
    category: 'expenses',
    emoji: '__settled__',
    type: 'expense',
    deepLink: { tab: 'expenses', ...(settleSection ? { section: settleSection } : {}) },
  });
}

/** Trip base currency changed — FYI, no push (P2). */
export async function notifyBaseCurrencyChanged(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  allMemberIds: string[],
  newCurrency: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: allMemberIds,
    senderId,
    senderName: fn(senderName),
    title: 'Base currency changed',
    message: `${fn(senderName)} changed the _${tripTitle}_ trip currency to **${newCurrency}**`,
    priority: 'P2',
    category: 'expenses',
    type: 'expense',
    deepLink: { tab: 'expenses' },
  });
}

/** Itinerary: someone added a note to a day. */
export async function notifyDayNoteAdded(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  dayTravellerIds: string[],
  location: string,
  dayDate: string,
  itemId?: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: dayTravellerIds,
    senderId,
    senderName: fn(senderName),
    title: 'New note',
    message: `${fn(senderName)} added **a note** to your ${location} itinerary`,
    priority: 'P2',
    category: 'shortlist',
    emoji: '__note__',   // sentinel → StickyNote Lucide icon, matching the Notes section
    type: 'itinerary',
    deepLink: { tab: 'bucketlist', dayDate, itemId },
  });
}


/** Trip admin toggled a traveller on or off for a destination. */
export async function notifyTravellerToggled(
  tripId: string,
  tripTitle: string,
  senderId: string,
  senderName: string,
  recipientId: string,
  city: string,
  added: boolean,
  dayDate: string
) {
  await notifyUsers({
    tripId, tripTitle,
    recipientIds: [recipientId],
    senderId,
    senderName: fn(senderName),
    title: added ? 'Added to destination' : 'Removed from destination',
    message: added
      ? `${fn(senderName)} has added you to the **${city}** itinerary — you'll be notified of updates for this part of the trip.`
      : `${fn(senderName)} has removed you from the **${city}** itinerary — you'll no longer receive updates for this part of the trip.`,
    priority: 'P1',
    category: 'trip-admin',
    emoji: added ? '__added__' : '__removed__',
    type: 'info',
    deepLink: { tab: 'bucketlist', dayDate },
  });
}

// ─── Legacy shim (used in a few places still, gradually replace) ─────────────
export async function notifyTripMembers(
  tripId: string,
  memberIds: string[],
  creatorId: string,
  title: string,
  message: string,
  type: 'info' | 'invite' = 'info',
  data: any = {}
) {
  await createTripNotifications({
    tripId,
    recipientIds: memberIds.filter(id => id !== creatorId),
    title,
    message,
    type,
    category: 'trip-admin',
    priority: type === 'invite' ? 'P1' : 'P2',
    actionRequired: type === 'invite',
    data: { ...data, creatorId, senderName: '' },
  });
}
